import { useEffect, useMemo, useRef, useState } from 'react';
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString, AsYouType } from 'libphonenumber-js/max';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

const COUNTRIES = getCountries()
  .map((code) => ({ code, name: regionNames.of(code), dial: getCountryCallingCode(code) }))
  .sort((a, b) => a.name.localeCompare(b.name));

export const DEFAULT_COUNTRY = 'AE';

export function Flag({ code }) {
  return (
    <img
      src={`https://flagcdn.com/w20/${code.toLowerCase()}.png`}
      srcSet={`https://flagcdn.com/w40/${code.toLowerCase()}.png 2x`}
      width="20"
      height="15"
      alt=""
      className="inline-block h-[15px] w-5 shrink-0 rounded-[2px] object-cover"
    />
  );
}

/** Returns an error message, or null when the value is empty or a valid number. */
export function validatePhone(value) {
  if (!value) return null;
  const parsed = parsePhoneNumberFromString(value);
  return parsed?.isValid() ? null : 'Enter a valid mobile number for the selected country';
}

/**
 * Value is stored as an E.164 string (e.g. "+971501234567") so the selected
 * country code travels with the number. Empty string when no digits entered.
 */
export default function PhoneInput({ label, value, onChange, error, required, id, defaultCountry = DEFAULT_COUNTRY }) {
  // Numbers saved before country codes existed have no "+"; read them as national numbers of the default country.
  const parsed = useMemo(() => (value ? parsePhoneNumberFromString(value, defaultCountry) : null), [value, defaultCountry]);
  const [country, setCountry] = useState(parsed?.country || defaultCountry);
  const [digits, setDigits] = useState(parsed ? parsed.nationalNumber : '');
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [touched, setTouched] = useState(false);
  const rootRef = useRef(null);

  // Reset local state if the parent clears or replaces the value externally.
  useEffect(() => {
    if (!value) {
      setDigits('');
    } else if (parsed && (parsed.nationalNumber !== digits.replace(/\D/g, '') || (parsed.country && parsed.country !== country))) {
      setDigits(parsed.nationalNumber);
      if (parsed.country) setCountry(parsed.country);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => rootRef.current && !rootRef.current.contains(e.target) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const emit = (nextCountry, nextDigits) => {
    const clean = nextDigits.replace(/\D/g, '');
    onChange(clean ? `+${getCountryCallingCode(nextCountry)}${clean}` : '');
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^\+/, '');
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(q) || c.dial.startsWith(q) || c.code.toLowerCase() === q);
  }, [query]);

  const localError = touched ? validatePhone(value) : null;
  const shownError = error || localError;
  const inputId = id || label?.toLowerCase().replace(/\W+/g, '_');

  return (
    <div ref={rootRef} className="relative">
      {label && (
        <label htmlFor={inputId} className="mb-1 block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-600"> *</span>}
        </label>
      )}
      <div
        className={`flex rounded-md border focus-within:border-primary focus-within:ring-2 focus-within:ring-primary ${
          shownError ? 'border-red-400' : 'border-gray-300'
        }`}
      >
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex shrink-0 items-center gap-1.5 rounded-l-md border-r border-gray-200 bg-gray-50 px-2 text-sm text-gray-700"
          aria-label="Select country code"
        >
          <Flag code={country} />
          <span>+{getCountryCallingCode(country)}</span>
          <span className="text-[10px] text-gray-400">▾</span>
        </button>
        <input
          id={inputId}
          type="tel"
          inputMode="tel"
          autoComplete="off"
          value={digits}
          required={required}
          placeholder="Mobile number"
          onChange={(e) => {
            const formatted = new AsYouType(country).input(e.target.value.replace(/[^\d\s-]/g, ''));
            setDigits(formatted);
            emit(country, formatted);
          }}
          onBlur={() => setTouched(true)}
          className="min-w-0 flex-1 rounded-r-md px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
        />
      </div>

      {open && (
        <div className="absolute z-30 mt-1 w-72 rounded-md border border-gray-200 bg-white shadow-lg">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search country or code"
            className="w-full rounded-t-md border-b border-gray-200 px-3 py-2 text-sm focus:outline-none"
          />
          <ul className="max-h-60 overflow-y-auto py-1">
            {filtered.map((c) => (
              <li key={c.code}>
                <button
                  type="button"
                  onClick={() => {
                    setCountry(c.code);
                    setOpen(false);
                    setQuery('');
                    const reformatted = digits ? new AsYouType(c.code).input(digits.replace(/\D/g, '')) : '';
                    setDigits(reformatted);
                    emit(c.code, reformatted);
                  }}
                  className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50 ${
                    c.code === country ? 'bg-primary-50' : ''
                  }`}
                >
                  <Flag code={c.code} />
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="text-gray-500">+{c.dial}</span>
                </button>
              </li>
            ))}
            {filtered.length === 0 && <li className="px-3 py-2 text-sm text-gray-500">No match</li>}
          </ul>
        </div>
      )}
      {shownError && <p className="mt-1 text-sm text-red-600">{shownError}</p>}
    </div>
  );
}
